import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS } from 'common/constants/defaultTableParamsConstants'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import type { EncounterLabApiItem } from 'features/Procedure/state/procedure-api.type'
import { useFetchEncounterLabsQuery } from 'features/Procedure/state/procedure.api'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import classNames from 'classnames'
import { labsTableColumns } from './columns'
import { LabsPreview } from './preview'
import styles from './labs.module.scss'

export type LabsComparisonProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  className?: string
}

export const LabsComparison = ({
  selectedPatientId,
  selectedEncounterUid,
  className,
}: LabsComparisonProps) => {
  const { siteId } = useParams<{ siteId: string }>()
  const [selectedLabId, setSelectedLabId] = useState<number | null>(null)
  const [columns, setColumns] = useState<ObjectType[]>([])
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })

  const shouldSkipLabsQuery = !siteId || !selectedPatientId || !selectedEncounterUid

  const {
    data: labsData = [],
    isFetching: isFetchingLabs,
    isLoading: isLoadingLabs,
  } = useFetchEncounterLabsQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipLabsQuery,
    },
  )

  const handleSelectLab = useCallback((lab: EncounterLabApiItem) => {
    setSelectedLabId(lab.id)
  }, [])

  useEffect(() => {
    if (labsData.length > 0 && !selectedLabId) {
      setSelectedLabId(labsData[0].id)
    } else if (labsData.length === 0) {
      setSelectedLabId(null)
    }
  }, [labsData, selectedLabId])

  useEffect(() => {
    if (selectedLabId && labsData.length > 0) {
      const stillExists = labsData.find((lab) => lab.id === selectedLabId)
      if (!stillExists && labsData.length > 0) {
        setSelectedLabId(labsData[0].id)
      }
    }
  }, [labsData, selectedLabId])

  useEffect(() => {
    setColumns(
      labsTableColumns({
        selectedLabId,
        onSelectLab: handleSelectLab,
      }),
    )
  }, [selectedLabId, handleSelectLab])

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    setTableParams((prev) => ({ ...prev, total: labsData.length }))
  }, [labsData.length])

  const getRowClassName = useCallback(
    (record: EncounterLabApiItem) => {
      return selectedLabId === record.id ? styles.selectedRow : ''
    },
    [selectedLabId],
  )

  return (
    <div className={classNames(styles.comparisonContainer, className)}>
      <div className={styles.tableSection}>
        <Table
          itemKey='id'
          tableParams={tableParams}
          dataSource={labsData}
          setTableParams={setTableParams}
          columns={columns}
          isLoading={isFetchingLabs || isLoadingLabs}
          haveActions={false}
          pagination={false}
          rowClassName={getRowClassName}
        />
      </div>
      <div className={styles.previewSection}>
        <LabsPreview
          documentId={selectedLabId || undefined}
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
        />
      </div>
    </div>
  )
}
